import { useState } from 'react';
import { Directory, type DirectoryType, InspectOverlay } from '../directory.tsx';
import { Button, Empty, Mono, Section } from '../ui.tsx';

/**
 * Hardware — "what you have": the Duress hardware, vehicles and integrations
 * that plug the wider Duress ecosystem into this org's EVAC account, on the
 * shared rail-directory shell (`directory.tsx`) so it reads as the same shape as
 * People/Locations/Activity: a **type column** of categories, a **list** of what
 * is connected in the selected category, and a right **inspect overlay** for the
 * connect path.
 *
 * There is deliberately **no backing model yet**. EVAC is greenfield and the
 * device / vehicle / integration primitives live in the broader Duress platform,
 * not here — so this screen invents nothing (guardrail G20: no fabricated rows
 * presented as fact). Each category reads as honestly empty, and the one real
 * next step — connecting through the Duress team — is stated plainly and also
 * offered as the header's Connect action, which opens the overlay with the how.
 * When the integration ships, this is its nav home and the empty states fill
 * with real records the overlay then inspects.
 */
export function Hardware() {
  const [category, setCategory] = useState('all');
  const [connecting, setConnecting] = useState(false);

  const types: DirectoryType[] = [
    { id: 'all', label: 'All hardware' },
    { id: 'devices', label: 'Duress devices' },
    { id: 'vehicles', label: 'Vehicles' },
    { id: 'integrations', label: 'Integrations' },
  ];

  const showDevices = category === 'all' || category === 'devices';
  const showVehicles = category === 'all' || category === 'vehicles';
  const showIntegrations = category === 'all' || category === 'integrations';

  return (
    <>
      <Directory
        title="Hardware"
        typeColumnLabel="Hardware categories"
        types={types}
        activeType={category}
        onSelectType={setCategory}
        actions={
          <Button variant="primary" onClick={() => setConnecting(true)}>
            Connect
          </Button>
        }
      >
        {/* The connect path, stated in full and always visible — nothing here is
            hidden behind the Connect action, which only adds the step-by-step. */}
        <p className="mb-6 max-w-[640px] text-[14px] text-[var(--ink-2)]">
          Panic buttons, wearables and beacons, your vehicle fleet, and the systems you already run
          all connect here, so an alert can reach the hardware in the room and the vans on the road,
          not only the phones on your list. Nothing is connected yet — integrations are set up with
          the Duress team, so email <Mono>support@duress.com</Mono> and we will bring your devices,
          vehicles and systems into EVAC. This screen fills in as each one connects.
        </p>

        {/* Each category as an honest empty state — the shape the data will take,
            stated before there is any. The moment real records arrive, each
            becomes the list it describes, and a row opens the inspect overlay. */}
        {showDevices ? (
          <Section title="Duress devices">
            <Empty>
              No devices connected. Panic buttons, wearables and beacons appear here once linked to
              this organisation.
            </Empty>
          </Section>
        ) : null}

        {showVehicles ? (
          <Section title="Vehicles">
            <Empty>
              No vehicles connected. Bring in a fleet to alert and account for the vehicles on the
              road alongside the buildings.
            </Empty>
          </Section>
        ) : null}

        {showIntegrations ? (
          <Section title="Integrations">
            <Empty>
              Nothing connected. Link the systems you already run so EVAC reaches them when
              something happens.
            </Empty>
          </Section>
        ) : null}
      </Directory>

      {connecting ? (
        <InspectOverlay
          ariaLabel="Connect the Duress ecosystem"
          title="Connect hardware"
          subtitle="Devices, vehicles and integrations"
          onClose={() => setConnecting(false)}
        >
          <p className="max-w-prose text-[14px] text-[var(--ink-2)]">
            Integrations are set up with the Duress team rather than self-served, so nothing here
            can be operated on an empty account. Email <Mono>support@duress.com</Mono> with what you
            want to bring in — panic buttons, wearables and beacons, a vehicle fleet, or a system
            you already run — and we will connect it to this organisation. Once it is connected, it
            appears in its category on this screen, and opening a row shows its detail and the
            controls to manage it.
          </p>
        </InspectOverlay>
      ) : null}
    </>
  );
}
